import React, {useEffect, useState, useMemo, useCallback} from "react";
import {Button, Header, Pagination, PropertyFilter, SpaceBetween, Table} from "@amzn/awsui-components-react";
import {useCollection} from "@amzn/awsui-collection-hooks";
import {getMatchesCountText, PROPERTY_FILTERING_I18N_CONSTANTS} from "../../../phastos/table-config";
import {getAllReportingAggregatedDataForDST, getReportingAggregatedDataForDST} from "../redux/exceptionAPIs";
import {ACCOUNTING_TAB_FILTERING_PROPERTIES, INITIAL_PAGE_NUMBER, INITIATE_ADJUSTMENT, TAX_ADJUSTMENT_JOB_TYPE} from "./Constants";
import {downloadJsonAsExcel} from "../../utils/utils";
import {OBLIGATION_STATUS} from "../common/constants";
import {getUserAlias} from "../DS-V1/utils/utils-v1";
import {takeActionForWorkflow} from "../../../phastos/components/phastosAPIs";
import {normalizePageData} from "../../../../common/utils/commonUtils";
import {TableEmptyState, useDSTAccountingColumnDefinition} from "./Constants"
import {isNil} from "lodash";

const TableHeader = ({onRefresh, onDownload, downloadLoading, lastUpdatedOn, disableJournalPosting, handlePostJournal, isJournalPostButtonLoading}) => (
    <Header
        actions={
            <SpaceBetween size="xs" direction="horizontal">
                <Button onClick={onRefresh}>Refresh</Button>
                <Button onClick={onDownload} loading={downloadLoading} disabled={downloadLoading}>{downloadLoading ? 'Downloading...' : 'Download All Data'}</Button>
                <Button loading={isJournalPostButtonLoading} disabled={disableJournalPosting} onClick={handlePostJournal}>Post Journal</Button>
            </SpaceBetween>
        }
        description={`Last updated: ${lastUpdatedOn}`}
    >
        Accounting Summary
    </Header>
);

const usePaginatedData = ({ parentWorkflowId, useCase, setLastUpdatedOn }) => {
    const [data, setData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
    const [currentPageNumber, setCurrentPageNumber] = useState(INITIAL_PAGE_NUMBER);
    const [currentPageData, setCurrentPageData] = useState([]);
    const [hasMorePages, setHasMorePages] = useState(true);
    const [maxPageReached, setMaxPageReached] = useState(1);
    const [isInitialized, setIsInitialized] = useState(false);

    const getPageData = useCallback(
        (pageNumber, dataObj = data) => {
            return normalizePageData(dataObj?.[pageNumber]);
        },
        [data]
    );

    const resetPagination = useCallback(() => {
        setCurrentPageNumber(INITIAL_PAGE_NUMBER);
        setMaxPageReached(1);
        setLastEvaluatedKey(null);
        setHasMorePages(true);
    }, []);

    const executeFetch = useCallback(
        async ({ pageNumber, dataOverride, lastKeyOverride }) => {
            if (isLoading) return;

            setIsLoading(true);

            const request = {
                data: dataOverride,
                setData,
                setLastUpdatedOn,
                lastEvaluatedKey: lastKeyOverride,
                setLastEvaluatedKey,
                parentWorkflowId,
                useCase,
                currentPageNumber: pageNumber,
                setOpenEnd: setHasMorePages,
                setLoading: setIsLoading,
            };

            try {
                await getReportingAggregatedDataForDST(request);
            } catch (error) {
                console.error("Error fetching DST data:", error);
                setIsLoading(false);
            }
        }, [isLoading, parentWorkflowId, useCase, setLastUpdatedOn]);

    /**
     * Pagination fetch
     */
    const fetchData = useCallback(
        (pageNumber, isRefresh = false) => {
            executeFetch({
                pageNumber,
                dataOverride: isRefresh ? {} : data,
                lastKeyOverride: isRefresh ? null : lastEvaluatedKey,
            });
        }, [executeFetch, data, lastEvaluatedKey]);

    /**
     * Initial fetch (runs once)
     */
    useEffect(() => {
        if (!isInitialized) {
            setIsInitialized(true);
            executeFetch({
                pageNumber: INITIAL_PAGE_NUMBER,
                dataOverride: {},
                lastKeyOverride: null,
            });
        }
    }, [isInitialized, executeFetch]);

    const handlePageChange = useCallback(
        (pageNumber) => {
            if (pageNumber < 1) return;
            if (!hasMorePages && pageNumber > maxPageReached) return;

            setCurrentPageNumber(pageNumber);

            if (isNil(data[pageNumber])) {
                fetchData(pageNumber);
            } else {
                setCurrentPageData(getPageData(pageNumber, data));
                setIsLoading(false);
            }
        }, [hasMorePages, maxPageReached, data, fetchData, getPageData]);

    const refresh = useCallback(() => {
        resetPagination();
        setData({});
        setCurrentPageData([]);
        fetchData(INITIAL_PAGE_NUMBER, true);
    }, [resetPagination, fetchData]);

    // Update current page data when data changes
    useEffect(() => {
        if (!isNil(data[currentPageNumber])) {
            setCurrentPageData(getPageData(currentPageNumber, data));
            setIsLoading(false);
            setMaxPageReached((prev) => Math.max(prev, currentPageNumber));
        }
    }, [data, currentPageNumber, getPageData]);

    return {
        isLoading,
        currentPageNumber,
        currentPageData,
        hasMorePages,
        maxPageReached,
        handlePageChange,
        refresh,
    };
};

function DSTAccountingSummary({
                                  parentWorkflowId,
                                  useCase,
                                  lastUpdatedOn,
                                  setLastUpdatedOn,
                                  obligationStatus,
                                  dstDispatcher,
                                  jobDetails,
                                  fetchJobDetails,
                              }) {
    const [selectedItems, setSelectedItems] = useState([]);
    const [isJournalPostButtonLoading, setIsJournalPostButtonLoading] = useState(false);
    const taxAdjustmentJobData = useMemo(() => {
        return (jobDetails.adhocJobs || []).filter(
            job =>
                job.jobType === TAX_ADJUSTMENT_JOB_TYPE &&
                job.parentWorkflowId === parentWorkflowId
        );
    }, [jobDetails, parentWorkflowId]);


    const disableJournalPosting = useMemo(() => {
        return (
            isJournalPostButtonLoading ||
            obligationStatus !== OBLIGATION_STATUS.UnderAdjustment ||
            taxAdjustmentJobData.length > 0
        );
    }, [isJournalPostButtonLoading, obligationStatus, taxAdjustmentJobData]);


    const {
        isLoading,
        currentPageNumber,
        currentPageData,
        hasMorePages,
        maxPageReached,
        handlePageChange,
        refresh,
    } = usePaginatedData({
        parentWorkflowId,
        useCase,
        setLastUpdatedOn,
    });

    const columnDefinitions = useDSTAccountingColumnDefinition();

    // Collection configuration
    const collectionConfig = useMemo(
        () => ({
            filtering: {
                empty: <TableEmptyState type="noSummary"/>,
                noMatch: <TableEmptyState type="noMatches"/>,
            },
            propertyFiltering: {
                filteringProperties: ACCOUNTING_TAB_FILTERING_PROPERTIES,
                empty: <TableEmptyState type="noMatches"/>,
                noMatch: <TableEmptyState type="noMatches"/>,
            },
            sorting: {
                defaultState: {
                    sortingColumn: {sortingField: "month"},
                    isDescending: true,
                },
            },
            selection: {},
        }),
        []
    );

    const {items, filteredItemsCount, collectionProps, propertyFilterProps} =
        useCollection(currentPageData, collectionConfig);

    // Event handlers
    const handleSelectionChange = useCallback(({detail}) => {
        setSelectedItems(detail.selectedItems);
    }, []);

    const handlePaginationChange = useCallback(
        (event) => {
            handlePageChange(event.detail.currentPageIndex);
        },
        [handlePageChange]
    );

    const [downloadLoading, setDownloadLoading] = useState(false);

    const handlePostJournal = useCallback(async () => {
        if (disableJournalPosting) {
            return;
        }
        setIsJournalPostButtonLoading(true);
        try {
            const userAlias = await getUserAlias();
            const request = {
                workflowId: parentWorkflowId,
                actionType: INITIATE_ADJUSTMENT,
                requestor: userAlias,
            };

            const onSuccess = async () => {
                await fetchJobDetails();
            };

            await takeActionForWorkflow(dstDispatcher, request, onSuccess);
        } catch (e) {
            console.error("Post Journal failed", e);
        } finally {
            setIsJournalPostButtonLoading(false);
        }
    }, [disableJournalPosting, parentWorkflowId, dstDispatcher, fetchJobDetails]);

    const handleDownload = async () => {
        setDownloadLoading(true);
        try {
            const request = {
                parentWorkflowId,
                useCase
            };

            const allData = await getAllReportingAggregatedDataForDST(request);
            const flatData = getFlatAccountingAllData(allData);
            downloadJsonAsExcel(flatData, "DST Accounting Report");
        } catch (error) {
            console.error("Error downloading DST Accounting Report:", error);
        } finally {
            setDownloadLoading(false);
        }
    };

    function getFlatAccountingAllData(dataList) {
        const accountingDataList = [];

        for (let i = 0; i < dataList.length; i++) {
            const curRow = JSON.parse(dataList[i]); // Parse JSON string from API response

            const accountingCurRow = {
                "Month": curRow?.month,
                "Category": curRow?.businessCategory,
                "Legal Entity": curRow?.legalEntity,
                "Company Code": curRow?.companyCode,
                "Channel": curRow?.inputChannel,
                "Sub Business": curRow?.subBusiness,
                "Debit Or Credit": curRow?.debitOrCredit,
                "Account Number": curRow?.inputAccountNumber,
                "Cost Center": curRow?.inputCostCenter,
                "Location": curRow?.inputLocation,
                "Product Line": curRow?.inputProductLine,
                "Project": curRow?.inputProject,
                "Set Of Books": curRow?.setOfBooks,
                "GL Line Description": curRow?.glLineDescription,
                "Functional Amount": curRow?.dstBaseAmountDstReportingCurrency,
                "Functional Amount Currency": curRow?.dstReportingCurrency,
                "DST Liability (Reporting Currency)": curRow?.dstBaseAmountDstReportingCurrency,
                "DST Reporting Currency": curRow?.dstReportingCurrency,
            };

            accountingDataList.push(accountingCurRow);
        }

        return accountingDataList;
    }

    return (
        <SpaceBetween size="xl">
            <Table
                {...collectionProps}
                loading={isLoading}
                sortingDisabled={false}
                selectionType="single"
                selectedItems={selectedItems}
                onSelectionChange={handleSelectionChange}
                header={<TableHeader onRefresh={refresh} onDownload={handleDownload} downloadLoading={downloadLoading}
                                     lastUpdatedOn={lastUpdatedOn} disableJournalPosting={disableJournalPosting}
                                     handlePostJournal={handlePostJournal} isJournalPostButtonLoading={isJournalPostButtonLoading}/>}
                columnDefinitions={columnDefinitions}
                resizableColumns={false}
                items={items}
                filter={
                    <PropertyFilter
                        i18nStrings={PROPERTY_FILTERING_I18N_CONSTANTS}
                        filteringProperties={propertyFilterProps.filteringProperties}
                        filteringOptions={propertyFilterProps.filteringOptions}
                        onChange={propertyFilterProps.onChange}
                        query={propertyFilterProps.query}
                        countText={getMatchesCountText(filteredItemsCount)}
                    />
                }
                pagination={
                    <Pagination
                        currentPageIndex={currentPageNumber}
                        onChange={handlePaginationChange}
                        pagesCount={maxPageReached}
                        openEnd={hasMorePages}
                        disabled={isLoading}
                    />
                }
            />
        </SpaceBetween>
    );
}

export default DSTAccountingSummary;